import { FC, FormEvent } from 'react'

import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue, // useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { GENDER, IDocument } from 'entities/user/module'

import { MAX_YEARS_DURATIONS } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { REGEX_EMAIL_NUMBER, REGEX_NAME } from 'shared/constants/regex'
import { addDate, dateFormat } from 'shared/lib/dateFormat'
// import { CheckBox } from 'shared/ui/check-box'
import { DatePickerWithLabel } from 'shared/ui/date-picker-label'
import { GenderSelect } from 'shared/ui/gender-select'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import { Option } from 'shared/ui/select'
import { SelectWithLabel } from 'shared/ui/select-label'

import s from './styles.module.scss'

export interface IDocumentForm {
  form: IDocument & { hasMiddle?: boolean }
}

export interface Props {
  register?: UseFormRegister<IDocumentForm>
  control?: Control<IDocumentForm>
  errors?: FieldErrors<IDocumentForm>
  setValue: UseFormSetValue<IDocumentForm>
  options: {
    showLabel?: boolean
    citizenships?: Option[]
  }
}

const rules = { minLength: 2, maxLength: 30 }

export const EditDetailForm: FC<Props> = observer(
  ({ control, options, errors, setValue }) => {
    const { t } = useTranslation()

    const { citizenships } = options
    // const hasMiddleName = useWatch({ control, name: 'form.hasMiddle' })

    const maxDateExpiration = addDate({
      count: MAX_YEARS_DURATIONS,
      type: 'year',
    })

    const onChangeInput = (event: FormEvent<HTMLInputElement>) => {
      return event?.currentTarget?.value?.toUpperCase().replace(REGEX_NAME, '')
    }

    const onChangeInputNumber = (event: FormEvent<HTMLInputElement>) => {
      return event?.currentTarget?.value
        ?.toUpperCase()
        .replace(REGEX_EMAIL_NUMBER, '')
    }

    return (
      <Paper>
        <div className={s.wrapper}>
          <Controller
            control={control}
            name="form.last_name"
            rules={{ required: true, ...rules }}
            render={({ field }) => (
              <InputWithLabel
                ref={field.ref}
                value={field?.value}
                label={t('form.last_name')}
                error={errors?.form?.last_name}
                onChange={(event) => field?.onChange(onChangeInput(event))}
              />
            )}
          />

          <Controller
            control={control}
            name="form.first_name"
            rules={{ required: true, ...rules }}
            render={({ field }) => (
              <InputWithLabel
                ref={field.ref}
                value={field?.value}
                label={t('form.first_name')}
                error={errors?.form?.first_name}
                onChange={(event) => field?.onChange(onChangeInput(event))}
              />
            )}
          />

          <Controller
            name="form.gender"
            control={control}
            rules={{ required: t('select_gender') }}
            render={({ field }) => (
              <GenderSelect
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                error={errors?.form?.gender}
                value={field.value as GENDER}
              />
            )}
          />

          <Controller
            name="form.date_of_birth"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DatePickerWithLabel
                {...field}
                error={errors?.form?.date_of_birth}
                label={t('birth_day')}
                value={field.value ? new Date(field.value) : undefined}
                onChange={(date) => {
                  field.onChange(
                    dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE),
                  )
                }}
              />
            )}
          />

          <Controller
            name="form.citizenship"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              return (
                <SelectWithLabel
                  {...field}
                  ref={field?.ref}
                  value={field?.value}
                  options={citizenships}
                  label={t('citizenship')}
                  onChange={(option) => {
                    field.onChange(option.value)

                    setValue('form.document_type', '')
                    setValue('form.document_number', '')
                  }}
                  error={errors?.form?.citizenship}
                />
              )
            }}
          />

          <Controller
            control={control}
            name="form.document_number"
            rules={{ required: true, ...rules }}
            render={({ field }) => (
              <InputWithLabel
                ref={field.ref}
                value={field?.value}
                label={t('serial_documents')}
                error={errors?.form?.document_number}
                onChange={(event) =>
                  field?.onChange(onChangeInputNumber(event))
                }
              />
            )}
          />

          <Controller
            name="form.document_expire"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DatePickerWithLabel
                {...field}
                minDate={new Date()}
                maxDate={maxDateExpiration}
                rangeYear={MAX_YEARS_DURATIONS}
                label={t('expiration_date')}
                value={field.value ? new Date(field.value) : undefined}
                error={errors?.form?.document_expire}
                onChange={(date) => {
                  field.onChange(
                    dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE),
                  )
                }}
              />
            )}
          />

          {/* <Controller
            control={control}
            name="form.hasMiddle"
            render={({ field }) => (
              <CheckBox
                isChecked={!!field?.value}
                title={t('does_middle_name')}
                onClick={(value) => {
                  field.onChange(value)
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="form.middle_name"
            rules={{
              required: hasMiddleName,
              ...rules,
            }}
            render={({ field }) => (
              <div className={clsx(!hasMiddleName && s.disabledField)}>
                <Input
                  disabled={!hasMiddleName}
                  ref={field.ref}
                  value={field?.value}
                  placeholder={t('form.middle_name')}
                  error={errors?.form?.middle_name}
                  onChange={(event) => field?.onChange(onChangeInput(event))}
                />
              </div>
            )}
          /> */}
        </div>
      </Paper>
    )
  },
)
